import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

/** Shared tab layout for merged Advanced-mode pages (Stock, Invoices & Bills, Tax &
 * Currency, Fulfillment, Promotions, Team, Approvals, System — business-owner walkthrough
 * follow-up: "merge similar pages together"). Each tab renders an existing page component
 * largely unchanged; only the surrounding chrome is new. Active tab lives in `?tab=`, so
 * every tab is directly linkable/bookmarkable and old single-purpose routes can redirect
 * straight into a specific tab (see `App.tsx`). */

export interface TabDef {
  key: string;
  label: string;
  content: ReactNode;
}

export function TabbedPage({ title, tabs, defaultTab }: { title: string; tabs: TabDef[]; defaultTab?: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const activeTab = tabs.some((t) => t.key === requested) ? requested! : (defaultTab ?? tabs[0]?.key);

  // Deliberately no page/page__header wrapper of its own — every tab's `content` is an
  // existing page component that already renders its own `.page` + title (e.g. "Low
  // Stock"). Duplicating a "Stock" title here on top of that would just be two headings
  // stacked for one screen; the slim tab strip is the only new chrome, floating above
  // whichever existing page is currently selected.
  return (
    <div>
      <div className="sl-tab-strip" role="tablist" aria-label={title}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.key === activeTab}
            className={["sl-tab-strip__tab", tab.key === activeTab ? "sl-tab-strip__tab--active" : null]
              .filter(Boolean)
              .join(" ")}
            onClick={() =>
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", tab.key);
                return next;
              })
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.find((t) => t.key === activeTab)?.content}
    </div>
  );
}
